import { useShallow } from 'zustand/react/shallow';
import { useWorkspaceStore } from '@/features/workspace/core/store/workspaceStore';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { useScriptBrowserStore } from '@/features/script-browser/store/scriptBrowserStore';
import { resolveActiveLinkLabel } from '@/features/database-blueprint/utils/activeLink';
import { isConnectionEndpoint } from '@/features/workspace/core/types/workspaceTypes';
import { useDashboardStore } from '@/features/workspace/dashboards/store/dashboardStore';
import {
  tileRuntimeKey,
  useDashboardRuntimeStore,
} from '@/features/workspace/dashboards/store/dashboardRuntimeStore';
import { isQueryBackedTile } from '@/features/workspace/dashboards/types/dashboardTypes';
import type { WorkspaceConnectionNeed, BannerConnectionRef } from '../types';
import { safeConnectionName } from '@/types/safeConnectionDisplay';

type ConnectionLookup = Map<string, { label: string; engine: string }>;

function toRef(
  connectionId: string | undefined,
  byId: ConnectionLookup,
): BannerConnectionRef | null {
  if (!connectionId) return null;
  const c = byId.get(connectionId);
  if (!c) return null;
  return { connectionId, label: c.label, engine: c.engine };
}

export function useActiveTabConnectionNeed(): WorkspaceConnectionNeed {
  const tab = useWorkspaceStore((s) =>
    s.tabs.find((t) => t.id === s.activeTabId),
  );
  const connections = useConnectionStore((s) => s.connections);
  const dashboards = useDashboardStore((s) => s.dashboards);
  const tileRuntimeByKey = useDashboardRuntimeStore((s) => s.byTile);
  const mountedFolders = useScriptBrowserStore(
    useShallow((s) => {
      const active = s.activeWorkspaceId
        ? s.workspaces?.find((w) => w.id === s.activeWorkspaceId)
        : null;
      return active ? active.mountedFolders : s.defaultWorkspaceState.mountedFolders;
    }),
  );

  if (!tab) return { kind: 'none' };

  // Dashboard: tiles can span connections — surface the first one whose
  // tile runtime reports connection-lost (S3 stale-badge wiring). Tiles on
  // healthy connections keep rendering; there is no dashboard-wide error.
  if (tab.type === 'dashboard') {
    const dashboardId = tab.content.dashboardId;
    const dashboard = dashboards.find((d) => d.id === dashboardId);
    const lostTile = dashboard?.tiles.find(
      (t) =>
        isQueryBackedTile(t) &&
        tileRuntimeByKey[tileRuntimeKey(dashboard.id, t.id)]?.staleReason === 'connection-lost',
    );
    const byId: ConnectionLookup = new Map(
      connections.map((c) => [c.id, { label: safeConnectionName(c), engine: c.engine ?? 'mssql' }]),
    );
    const ref = toRef(
      lostTile && isQueryBackedTile(lostTile) ? lostTile.connectionId : undefined,
      byId,
    );
    return ref ? { kind: 'single', ref } : { kind: 'none' };
  }

  // Dual: schema-compare / data-compare — read from content endpoints
  if (tab.type === 'schema-compare') {
    const byId: ConnectionLookup = new Map(
      connections.map((c) => [c.id, { label: safeConnectionName(c), engine: c.engine ?? 'mssql' }]),
    );
    const src = toRef(tab.content.source?.connectionId, byId);
    const tgt = toRef(tab.content.target?.connectionId, byId);
    if (src && tgt) return { kind: 'dual', source: src, target: tgt };
    const single = src ?? tgt;
    return single ? { kind: 'single', ref: single } : { kind: 'none' };
  }

  if (tab.type === 'data-compare') {
    const byId: ConnectionLookup = new Map(
      connections.map((c) => [c.id, { label: safeConnectionName(c), engine: c.engine ?? 'mssql' }]),
    );
    const srcId = isConnectionEndpoint(tab.content.source) ? tab.content.source.connectionId : undefined;
    const tgtId = isConnectionEndpoint(tab.content.target) ? tab.content.target.connectionId : undefined;
    const src = toRef(srcId, byId);
    const tgt = toRef(tgtId, byId);
    if (src && tgt) return { kind: 'dual', source: src, target: tgt };
    const single = src ?? tgt;
    return single ? { kind: 'single', ref: single } : { kind: 'none' };
  }

  // Database Blueprint — resolve active link's connectionId from the script-browser folder
  if (tab.type === 'database-blueprint') {
    const byId: ConnectionLookup = new Map(
      connections.map((c) => [c.id, { label: safeConnectionName(c), engine: c.engine ?? 'mssql' }]),
    );
    const blueprintFolderId = tab.content.blueprintFolderId;
    const folder = mountedFolders.find(
      (f) => f.id === blueprintFolderId && f.kind === 'blueprint' && f.blueprintMetadata,
    );
    const linkedDbs = folder?.blueprintMetadata?.linkedDbs ?? [];
    const label = resolveActiveLinkLabel({
      activeLinkLabel: tab.content.selectedLinkedDbLabel ?? folder?.blueprintMetadata?.activeLinkLabel,
      linkedDbs,
    });
    const active = linkedDbs.find((l) => l.label === label);
    const ref = toRef(active?.connectionId, byId);
    return ref ? { kind: 'single', ref } : { kind: 'none' };
  }

  // Single-connection tabs (Query, TableExplorer, Designer, Plan, etc.)
  const byId: ConnectionLookup = new Map(
    connections.map((c) => [c.id, { label: safeConnectionName(c), engine: c.engine ?? 'mssql' }]),
  );
  const ref = toRef(tab.connectionInfo?.connectionId, byId);
  return ref ? { kind: 'single', ref } : { kind: 'none' };
}
